/**
 * Frontend-only value sets: UI state that no backend or worker ever sees.
 *
 * These deliberately live outside packages/contracts (whose enums are for
 * values shared across the API boundary) and carry no Zod schemas — nothing
 * validates them at runtime, they only drive rendering and navigation state.
 * The registry is a directory rather than one module because the 250-line file
 * cap makes a single file impossible; each module below owns one surface's
 * vocabulary.
 *
 * The same split applies to labels. Contracts owns the CANONICAL label for a
 * stored enum value (`contracts/enums/core-labels`), because the backend
 * renders those words into customer-facing artifacts — the issue workbook —
 * and a spreadsheet that disagreeing with the dashboard about the same issue
 * is a bug. Wording written for one surface's tone or layout stays here.
 */

// App shell state: keyboard scoping and the query cache namespaces.

export const KEYBOARD_SCOPES = ["global", "list", "drawer", "palette"] as const;
export const KEYBOARD_SCOPE = {
  GLOBAL: "global",
  LIST: "list",
  DRAWER: "drawer",
  PALETTE: "palette",
} as const satisfies Record<string, KeyboardScope>;
export type KeyboardScope = (typeof KEYBOARD_SCOPES)[number];

/**
 * The cache namespace a query read belongs to. Registered here so a surface
 * membership question ("is this a tone-of-voice read?") is answered from the
 * registry rather than from whether the key's text happens to start with a
 * prefix — which silently included any future key that shared those letters.
 */
const QUERY_ROOTS = [
  "benchmark",
  "content-fitness",
  "current-run",
  "metric-catalog",
  "overview",
  "page-detail",
  "playground-voices",
  "run-detail",
  "runs",
  "social-report",
  "tov-map-attempt",
  "tov-profiles",
  "tov-sources",
  "tov-version",
  "tov-versions",
  "websites",
] as const;
type QueryRoot = (typeof QUERY_ROOTS)[number];

export const QUERY_ROOT = {
  BENCHMARK: "benchmark",
  CONTENT_FITNESS: "content-fitness",
  CURRENT_RUN: "current-run",
  METRIC_CATALOG: "metric-catalog",
  OVERVIEW: "overview",
  PAGE_DETAIL: "page-detail",
  PLAYGROUND_VOICES: "playground-voices",
  RUN_DETAIL: "run-detail",
  RUNS: "runs",
  SOCIAL_REPORT: "social-report",
  TOV_MAP_ATTEMPT: "tov-map-attempt",
  TOV_PROFILES: "tov-profiles",
  TOV_SOURCES: "tov-sources",
  TOV_VERSION: "tov-version",
  TOV_VERSIONS: "tov-versions",
  WEBSITES: "websites",
} as const satisfies Record<string, QueryRoot>;

/**
 * Every namespace holding Client tone-of-voice reads. One edit here keeps the
 * workspace, Brand Guidelines and Scan summary refreshing together.
 */
export const TOV_QUERY_ROOTS: readonly string[] = [
  QUERY_ROOT.TOV_MAP_ATTEMPT,
  QUERY_ROOT.TOV_PROFILES,
  QUERY_ROOT.TOV_SOURCES,
  QUERY_ROOT.TOV_VERSION,
  QUERY_ROOT.TOV_VERSIONS,
];
